<template>
	<div>
		<v-card class="post-card elevation-0 d-flex flex-column">
			<div class="post-card__container">
				<div class="post-card__outer">
					<div
						class="d-flex post-card__list pl-0 pr-0"
						:style="{ paddingTop: post.sponsored_by ? '38px' : '12px' }"
					>
						<SponsorBadge
							v-if="sponsoredLabel"
							:text="sponsoredLabel"
							:top="'0px'"
							:right="'0px'"
							:icon="'star'"
							:iconSize="10"
							:iconColor="'#ffffff'"
						/>
						<div class="d-flex justify-space-between align-center px-3">
							<router-link
								:to="{
									name: isCurrentUser(this.post.user.user_id)
										? 'profile'
										: 'user-by-id',
									params: isCurrentUser(this.post.user.user_id)
										? []
										: { id: this.post.user.user_id },
								}"
								class="d-flex align-center text-decoration-none"
								:class="{ disabled: this.post.user.user_hidden }"
							>
								<Avatar
									class="mr-2 post-card__avatar"
									:size="50"
									:avatar="this.post.user.user_logo"
								/>
								<div
									class="d-flex flex-column justify-center gap-4 post-card__meta"
								>
									{{ this.post.user.visible_username }}
									<span
										v-if="this.post.user.second_profile_row"
										class="post-card__meta-bottom"
									>
										{{ this.post.user.second_profile_row }}
									</span>
								</div>
							</router-link>

							<div
								:style="{
									justifyContent: post.pin_to_top ? 'space-between' : 'flex-end',
								}"
								class="post-preview-card__time-container"
							>
								<Icon v-if="post.pin_to_top" color="var(--badge-pin-background)" :size="16" type="pinned" />
								<span class="post-preview-card__meta-time"> {{ dateCreated }}</span>
							</div>
						</div>

						<div
							class="pa-0 pt-3 post-preview-card__thumbnail-outer"
							v-if="
								!isPollPost &&
                (
                    (post.slider_images && post.slider_images.length) ||
                      post.thread_image ||
                      isYoutubeVideo ||
                      isVimeoVideo
                 )
							"
						>
							<div class="slider-container" v-if="isTextPost">
								<button v-if="slideButtons" class="prev-btn" @click="prevSlide">
									❮
								</button>
								<div class="slider">
									<img
										class="post-preview-card__thumbnail"
										:src="photos[currentIndex]"
										alt="thread image"
										loading="lazy"
										@click="openModal"
										@error="handleImageError"
									/>
								</div>
								<button v-if="slideButtons" class="next-btn" @click="nextSlide">
									❯
								</button>
								<div v-if="post.slider_images.length > 1" class="dots-container">
									<span
										v-for="(photo, index) in photos"
										:key="index"
										class="dot"
										:class="{ active: index === currentIndex }"
										@click="goToSlide(index)"
									>
									</span>
								</div>
							</div>

							<div
								v-if="isPreviewVimeoModeActive"
								class="post-preview-card__thumbnail__vimeo"
							>
								<img
									class="post-preview-card__thumbnail"
									:src="this.post.thread_image"
									alt="thread image"
									loading="lazy"
									@error="handleImageError"
								/>
								<span @click="playVideo"></span>
							</div>
							<iframe
								style="padding: 0 12px"
								v-if="!isPreviewVimeoModeActive && isVimeoVideo"
								:src="formateVimeoVideoLink"
								width="531"
								height="338"
								:ref="createUniqueIframeName(this.post)"
								allow="autoplay; encrypted-media"
								allowfullscreen
								@load="iframeLoaded"
							></iframe>
							<div
								v-if="isPreviewYoutubeModeActive"
								class="post-preview-card__thumbnail__youtube"
							>
								<img
									class="post-preview-card__thumbnail"
									:src="this.post.thread_image"
									alt="thread image"
									loading="lazy"
									@error="handleImageError"
								/>
								<span @click="playVideo"></span>
							</div>
							<iframe
								style="padding: 0 12px"
								v-if="!isPreviewYoutubeModeActive && isYoutubeVideo"
								:src="formateYoutubeVideoLink"
								width="531"
								height="338"
								:ref="createUniqueIframeName(this.post)"
								allow="autoplay; encrypted-media"
								@load="iframeLoaded"
								allowfullscreen
							></iframe>
						</div>
						<v-card-text class="post-card__title pb-0 pt-3 px-3">
							{{ this.post.thread_title }}
						</v-card-text>
						<v-card-text v-if="post.thread_text" class="px-3 post-card__content">
							<div
								class="post-card__content-inner"
								v-html="postDescription(post.thread_text)"
							/>
						</v-card-text>
            <LinkInfo
                v-if="!isPollPost && (metadata || threadActionLink)"
                @toggleLink="toggleLink"
                :metadata="metadata"
                :threadActionTitle="threadActionTitle"/>
						<Poll
							v-if="isPollPost"
							:poll="post.poll"
							:postId="post.thread_id"
							:post="post"
              :typeValue="'detail'"
						/>
						<div
							v-if="!this.isPollPost && this.post.uploads && this.post.uploads.length"
						>
							<router-link
								style="text-decoration: unset"
								:to="{
									name: 'post-file-by-id',
									params: { id: this.post.thread_id },
									query: {
										enable_all_comments: enableAllComments ? 1 : 0,
										thread_menu_id: thread_menu_id,
									},
								}"
							>
								<div class="d-flex align-center px-3 pt-3">
									<div
										v-for="attachment in post.uploads.slice(0, 6)"
										:key="attachment.upload_id"
										class="attachment-wrapper"
									>
										<PostAttachments
											backgroundColor="#F3F3F6"
											:attachment="attachment"
										/>
									</div>
									<div
										v-if="post.uploads.length > 6"
										class="more-attachments"
										style="cursor: pointer"
									>
										+{{ countAttachments }}
									</div>
								</div>
							</router-link>
						</div>

						<div
							class="post-card__meta-bottom d-flex align-center px-3 pt-3"
							v-if="this.post.tags.length !== 0"
						>
							<div
								class="post-preview-card__tags-container d-flex justify-start flex-wrap"
							>
								<span
									class="post-preview-card__tag__more"
									v-for="(tag, index) in tagShowArray"
									:key="index"
								>
									#{{ tag.tag }}
								</span>
								<span
									v-if="this.post.tags.length > 2 && !showAllTags"
									@click="tagsShow"
									class="post-preview-card__tag__more"
								>
									+ {{ countTags }} more
								</span>
								<span
									v-if="showAllTags"
									@click="tagsShow"
									style="width: 67px"
									class="post-preview-card__tag__more"
								>
									Hide
								</span>
							</div>
						</div>

						<v-divider class="my-3" />

						<div class="d-flex align-center justify-space-between px-3 pb-3">
							<div class="d-flex">
								<button
									v-if="enableLikesButton"
									type="button"
									class="post-preview-card__like-btn elevation-0 pa-0"
									@click="onLike"
								>
									<Icon
										type="hearth"
										:size="24"
										:color="isLiked ? 'var(--text_button_tint)' : '#9b9b9b'"
									/>
									<span
										v-if="displayLikes"
										:class="
											isLiked
												? 'post-preview-card__like-counter-active'
												: 'post-preview-card__like-counter'
										"
									>
										{{ this.likes }}
									</span>
								</button>
								<div
									v-if="
										enableAllComments && allowComments && displayCommentsCount
									"
									class="d-flex align-center justify-center"
									style="width: 64px"
								>
									<Icon type="comment" :size="24" color="#9b9b9b" :pr="4" />
									<span class="post-preview-card__like-counter">
										{{ this.commentsCount }}
									</span>
								</div>
							</div>
							<div class="d-flex justify-end">
								<div
									v-if="displayViews"
									class="d-flex align-center justify-center mr-4"
									style="width: 64px"
								>
									<Icon type="eye" :size="24" color="#9b9b9b" :pr="4" />
									<span class="post-preview-card__like-counter">
										{{ this.post.view_count }}
									</span>
								</div>
								<div @click="onFollow" style="cursor: pointer">
									<span
										class="post-preview-card__like-button d-flex align-center"
										style="cursor: pointer; user-select: none"
									>
										<Icon
											:type="this.isFollowed ? 'bookmark' : 'bookmark_empty'"
											:color="
												this.isFollowed
													? 'var(--text_button_tint)'
													: '#9b9b9b'
											"
											:size="24"
										/>
									</span>
								</div>
							</div>
						</div>

						<div
							class="post-card__comments-view"
							v-if="allowComments && enableAllComments"
						>
							<Comment
								v-for="item in normalizedComments"
								:key="item.id"
								:comment="item"
								:post="post"
							/>
						</div>

						<v-divider v-if="allowComments && enableAllComments" />

						<div v-if="allowComments && enableAllComments && !isCommentActive">
							<div v-if="isAuthenticated">
								<CommentForm :avatar="this.profile.user_logo" :parent="this.post" />
							</div>
						</div>
					</div>
				</div>
				<CreatePostModal
					v-on:closeModal="togglePostModal"
					:show="isPostModalShow"
					:post="post"
					edit
				/>
				<ConfirmActionModal
					:show="isDeleteModalShow"
					@closeModal="togglePostDeleteModal"
					@confirmAction="deletePost"
					title="Are you sure you want to delete this post? This action cannot be undone."
					actionName="Delete"
					:isLoading="isPostUpdating"
				/>
			</div>
		</v-card>
		<PhotoModal v-if="isModalOpen" :post="post" @close="isModalOpen = false" />
		<v-dialog v-if="mobileLink" v-model="mobileLink" max-width="588px" width="100%" persistent>
			<v-card class="exhibitor-page__modal">
				<div class="exhibitor-page__modal__title">
					This screen is not available in the Web App, but you can access it through the
					mobile app
				</div>
				<button
					type="button"
					@click="toggleCloseMobileLink"
					class="exhibitor-page__modal__btn"
				>
					OK
				</button>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { toggleDislike, toggleFollow, toggleLike, toggleUnFollow } from '@/api/post'
import { mapActions, mapGetters } from 'vuex'
import Avatar from '@/components/Avatar'
import pinnedIcon from '@/assets/images/icon-pinned.svg'
import likedIcon from '@/assets/images/postIcon/icon-liked.svg'
import likeIcon from '@/assets/images/postIcon/icon-like.svg'
import viewIcon from '@/assets/images/postIcon/icon-view.svg'
import commentIcon from '@/assets/images/postIcon/icon-comment.svg'
import commentedIcon from '@/assets/images/postIcon/icon-commented.svg'
import sponsoredStarIcon from '@/assets/images/icon-sponsored-star.svg'
import PostAttachments from '@/components/Post/PostAttachments'
import Comment from '@/components/Comment.vue'
import { normalizeComment } from '@/utils/helpers'
import Form from '@/mixins/Form'
import CommentForm from '@/components/Forms/CommentForm.vue'
import Poll from '@/components/Post/Poll.vue'
import CreatePostModal from '@/components/Modals/CreatePostModal'
import ConfirmActionModal from '../../components/Modals/ConfirmActionModal.vue'
import Dwh from '@/mixins/Dwh'
import PhotoModal from '@/components/PhotoModal.vue'
import linkifyThreadText from '@/mixins/linkifyThreadText'
import SponsorBadge from '@/components/SponsorBadge.vue'
import LinkInfo from "@/components/LinkInfo.vue"

Vue.use(VueScrollTo)

const THREAD_VIDEO_TYPE = 3
const VIDEO_TYPE_YOUTUBE = 1
const VIDEO_TYPE_VIMEO = 2
const THREAD_POLL_TYPE = 4

export default {
	components: {
		SponsorBadge,
		PhotoModal,
		CommentForm,
		Comment,
		Avatar,
		PostAttachments,
		Poll,
		CreatePostModal,
		ConfirmActionModal,
    LinkInfo
	},
	mixins: [Form, Dwh, linkifyThreadText],
	props: {
		post: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			mobileLink: false,
			showAllTags: false,
			metadata: null,
			thread_menu_id: +this.$route.query.thread_menu_id,
			isModalOpen: false,
			currentIndex: 0,
			isExpanded: false,
			isContentTooLong: false,
			offset: true,
			isLiked: false,
			isFollowed: false,
			likes: 0,
			commentsCount: 0,
			enableAllComments: false,
			notAvailableImage: require('@/assets/images/image_not_available.png'),
			pinnedIcon,
			likedIcon,
			commentIcon,
			commentedIcon,
			likeIcon,
			viewIcon,
			sponsoredStarIcon,
			previewMode: true,
			iframeElement: null,
		}
	},
	mounted() {
		this.fetchMetadata()
		this.$nextTick(() => {
			this.checkContentHeight()
		})
	},
	watch: {
		'post.thread_link': function (newUrl) {
			if (newUrl) {
				this.fetchMetadata()
			}
		},
		isMyPost: {
			handler(value) {
				this.myPost(value)
			},
			immediate: true,
		},
		isPollTypePost: {
			handler(value) {
				this.setTypePosts(value)
			},
			immediate: true,
		},
	},
	computed: {
		...mapGetters('post', [
			'isCommentLoading',
			'comments',
			'isCommentUpdating',
			'isPostUpdating',
		]),
		...mapGetters('auth', ['profile', 'isAuthenticated', 'mapRegisterToken']),
		...mapGetters('auth', ['isAuthenticated']),
		...mapGetters('settings', ['appCore']),
		...mapGetters('advertisement', ['isPostModalShow', 'isDeleteModalShow']),
		...mapGetters('forum', ['isCommentActive']),
		sponsoredLabel() {
			const sponsor = this.post.sponsored_by
			if (!sponsor) return ''

			const limitedSponsor = sponsor.length > 30 ? sponsor.slice(0, 30) + '…' : sponsor

			return `Sponsored by ${limitedSponsor}`
		},
		tagShowArray() {
			if (!this.showAllTags) {
				return this.post.tags.slice(0, 2)
			} else {
				return this.post.tags
			}
		},
		countTags() {
			if (Array.isArray(this.post.tags)) {
				return this.post.tags.length - 2
			}
			return 0
		},
		isYoutubeVideoWithPreviewImage() {
			const {
				thread_type: type,
				thread_image: image,
				thread_video_type: videoType,
			} = this.post || {}

			return type === THREAD_VIDEO_TYPE && videoType === VIDEO_TYPE_YOUTUBE && image
		},
		countAttachments() {
			if (Array.isArray(this.post.uploads)) {
				return this.post.uploads.length - 6
			}
			return 0
		},
		slideButtons() {
			return this.photos.length > 1
		},
		photos() {
			if (this.post.slider_images) {
				return this.post.slider_images.map((image) => image.image_url)
			}
			return []
		},
		whichView() {
			return this.view
		},
		isCurrentUserMentioned() {
			const currentUserId = this.profile.user_id
			return this.contentFormateChangeMentions.some((item) => {
				return item.link && item.link.params.id === currentUserId
			})
		},
		dateCreated() {
			const date = new Date(this.post.publish_timestamp * 1000)
			const options = { year: 'numeric', month: 'long', day: 'numeric' }
			return date.toLocaleDateString('en-US', options)
		},
		allowComments() {
			return !!this.post.allow_comments
		},

		/**
		 * @returns Array
		 */
		normalizedComments: function () {
			return this.comments.map((item) => normalizeComment(item))
		},
		isTextPost() {
			return this.post.thread_type !== THREAD_VIDEO_TYPE
		},
		isPollPost() {
			return this.post.thread_type === THREAD_POLL_TYPE
		},
		isPreviewVimeoModeActive() {
			if (this.isVimeoVideoWithPreviewImage) {
				if (this.previewMode) {
					return true
				}
			}
			return false
		},
		isVimeoVideoWithPreviewImage() {
			const {
				thread_type: type,
				thread_image: image,
				thread_video_type: videoType,
			} = this.post || {}

			return type === THREAD_VIDEO_TYPE && videoType === VIDEO_TYPE_VIMEO && image
		},
		isVimeoVideo() {
			const { thread_type: type, thread_video_type: videoType } = this.post || {}

			return type === THREAD_VIDEO_TYPE && videoType === VIDEO_TYPE_VIMEO
		},
		formateVimeoVideoLink() {
			const { thread_video_code: code } = this.post || {}

			const additionalParams = '?autoplay=1&mute=1'

			if (this.isVimeoVideoWithPreviewImage) {
				return `https://player.vimeo.com/video/${code}${additionalParams}`
			}

			return `https://player.vimeo.com/video/${code}`
		},
		isPreviewYoutubeModeActive() {
			if (this.isYoutubeVideoWithPreviewImage) {
				if (this.previewMode) {
					return true
				}
			}

			return false
		},
		isYoutubeVideo() {
			const { thread_type: type, thread_video_type: videoType } = this.post || {}

			return type === THREAD_VIDEO_TYPE && videoType === VIDEO_TYPE_YOUTUBE
		},
		formateYoutubeVideoLink() {
			const { thread_video_code: code } = this.post || {}

			const additionalParams = '?autoplay=1&mute=1'

			if (this.isYoutubeVideoWithPreviewImage) {
				return `https://www.youtube.com/embed/${code}${additionalParams}`
			}

			return `https://www.youtube.com/embed/${code}`
		},
		isMyPost() {
			return this.post.user.user_id === this.profile.user_id
		},
		isPollTypePost() {
			return this.post.thread_type === THREAD_POLL_TYPE
		},
		threadActionVisible() {
			return (
				this.post.threadActions.length &&
				!!this.post.threadActions[0].action_link &&
				!!this.post.threadActions[0].action_title
			)
		},
		threadActionTitle() {
			return this.threadActionVisible ? this.post.threadActions[0].action_title : null
		},
		threadActionLink() {
			if (!this.threadActionVisible) return null

			const link = this.post.threadActions[0].action_link
			const isExternal = link.includes('http://') || link.includes('https://')

			if (isExternal) return link
			const match = link.match(/:\/\/(.*)/)
			if (!match) return link

			let routePart = match[1]
			const needsAuthPrefix = [
				'restore-password',
				'login',
				'register',
				'welcome',
				'role-selection',
			]
			const [firstSegment] = routePart.split('/')
			if (needsAuthPrefix.includes(firstSegment)) {
				routePart = `auth/${routePart}`
			}

			return `${window.location.origin}/webapp/${routePart}`
		},
		currentMenuItem() {
			if (this.appCore) {
				const regex = /:\/\/(.+)/
				const currentMenuItem = this.appCore.app_menu.find((item) => {
					const match = item.url.match(regex)
					const result = match ? match[1] : null
					return (
						result === 'threads' &&
						item.params.thread_menu_id === +this.$route.query.thread_menu_id
					)
				})
				return currentMenuItem
			}
			return null
		},
		displayViews() {
			if (this.currentMenuItem) {
				return !!this.currentMenuItem.params.display_views
			}
			return false
		},
		displayLikes() {
			if (this.currentMenuItem) {
				return !!this.currentMenuItem.params.display_likes
			}
			return false
		},
		enableLikesButton() {
			if (this.currentMenuItem) {
				return !!this.currentMenuItem.params.enable_likes_button
			}
			return false
		},
		displayCommentsCount() {
			if (this.currentMenuItem) {
				return !!this.currentMenuItem.params.display_comments_count
			}
			return false
		},
	},
	methods: {
		...mapActions('post', ['getPost', 'removePost']),
		...mapActions('advertisement', [
			'togglePostModal',
			'togglePostDeleteModal',
			'setMyPosts',
			'setTypePosts',
		]),
		...mapActions('auth', ['getMapRegisterToken']),
		async toggleLink() {
			const target = this.metadata?.url || this.threadActionLink

			if (!target) return

			if (this.isUnsupportedDeepLink(target)) {
				this.mobileLink = true
				return
			}

			if (this.isMapUrlValid(target)) {
				try {
					await this.getMapRegisterToken()
					if (!this.mapRegisterToken) {
						console.error('Token not received')
						return
					}
					const urlWithToken = `${target}${target.includes('?') ? '&' : '?'}code=${
						this.mapRegisterToken
					}`
					window.open(urlWithToken, '_self')
				} catch (e) {
					console.error(e)
				}
			} else {
				window.open(target, '_self')
			}
		},
		toggleCloseMobileLink() {
			this.mobileLink = false
		},
		isTypePost(value) {
			this.setTypePosts(value)
		},
		myPost(value) {
			this.setMyPosts(value)
		},
		tagsShow() {
			this.showAllTags = !this.showAllTags
		},
		async fetchMetadata() {
			if (!this.post.thread_link) return
			try {
				const response = await fetch(this.post.thread_link, { mode: 'cors' })
				const html = await response.text()
				const parser = new DOMParser()
				const doc = parser.parseFromString(html, 'text/html')

				this.metadata = {
					title:
						doc.querySelector("meta[property='og:title']")?.content ||
						doc.title ||
						'',
					description:
						doc.querySelector("meta[property='og:description']")?.content ||
						this.getDisplayLink(this.post.thread_link),
					image: doc.querySelector("meta[property='og:image']")?.content || null,
					url: this.post.thread_link,
				}
			} catch (error) {
				console.error(error)
				this.metadata = {
					title: '',
					description: this.getDisplayLink(this.post.thread_link),
					image: null,
					url: this.post.thread_link,
				}
			}
		},

		openModal() {
			this.isModalOpen = true
		},
		nextSlide() {
			this.currentIndex = (this.currentIndex + 1) % this.photos.length
		},
		prevSlide() {
			this.currentIndex = (this.currentIndex - 1 + this.photos.length) % this.photos.length
		},
		goToSlide(index) {
			this.currentIndex = index
		},
		checkContentHeight() {
			const contentEl = this.$refs.contentInner
			if (contentEl) {
				const lineHeight = parseFloat(window.getComputedStyle(contentEl).lineHeight)
				const height = contentEl.scrollHeight
				const numLines = Math.floor(height / lineHeight)
				this.isContentTooLong = numLines > 5
			}
		},
		toggleExpand() {
			this.isExpanded = !this.isExpanded
		},
		getHrefLink(rawLink) {
			if (!rawLink) {
				return ''
			}
			const prefix = 'com.pinxterapp.dev.clowder-core://resources'
			if (rawLink.startsWith(prefix)) {
				const queryPart = rawLink.substring(prefix.length)
				return `${window.location.origin}/resources${queryPart}`
			} else {
				return rawLink
			}
		},
		getDisplayLink(rawLink) {
			if (!rawLink) {
				return ''
			}
			if (rawLink.startsWith('com.pinxterapp.dev.clowder-core://')) {
				return window.location.origin
			}
			try {
				const urlObj = new URL(rawLink)
				let domain = urlObj.hostname
				if (!domain.startsWith('www.')) {
					domain = 'www.' + domain
				}
				return domain
			} catch (error) {
				return rawLink
			}
		},

		navigateToComments() {
			if (!this.post.id) {
				return
			}

			this.$router.push({ name: 'SinglePost', params: { id: this.post.id } })
			setTimeout(() => {
				VueScrollTo.scrollTo('#comment-id', 1000)
			}, 500)
		},
		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const { user_id: currentUserId } = this.profile
			return userId === currentUserId
		},
		handleImageError() {
			this.post.thread_image = this.notAvailableImage
		},
		removeQueryParams(url) {
			if (!url) {
				return ''
			}

			let parsedUrl = new URL(url)
			parsedUrl.search = ''

			return parsedUrl.toString().slice(0, 65)
		},
		onLike() {
			if (this.isLiked) {
				if (this.likes === 0) {
					return
				}
				this.likes--
				toggleDislike(this.post.thread_id)
			} else if (!this.isLiked) {
				this.likes++
				toggleLike({ thread_id: this.post.thread_id })
				this.sendTrigger({
					category: 'Content',
					action: 'click_like_news_post',
					module: 'News Feed',
					data: this.post.thread_id,
					title: this.post.thread_title,
					content_chapter_id: this.post.chapter_id ? [this.post.chapter_id] : null,
				})
			}

			this.isLiked = !this.isLiked
		},
		onFollow() {
			if (this.isFollowed) {
				toggleUnFollow(this.post.thread_id)
			} else if (!this.isFollowed) {
				toggleFollow({ thread_id: this.post.thread_id })
				this.sendTrigger({
					category: 'Content',
					action: 'click_bookmark_news_post',
					module: 'News Feed',
					data: this.post.thread_id,
					title: this.post.thread_title,
					content_chapter_id: this.post.chapter_id ? [this.post.chapter_id] : null,
				})
			}

			this.isFollowed = !this.isFollowed
		},

		pinnedType(post) {
			let result = {}

			if (post.pin_to_top && post.sponsored_by) {
				return (result = {
					icon: sponsoredStarIcon,
					text: 'Sponsored by ' + post.sponsored_by,
				})
			} else if (post.sponsored_by) {
				return (result = {
					icon: sponsoredStarIcon,
					text: 'Sponsored by ' + post.sponsored_by,
				})
			} else if (post.pin_to_top) {
				result = { icon: pinnedIcon, text: 'Pinned' }
			} else {
				result = { icon: pinnedIcon, text: 'Pinned' }
			}

			return result
		},

		onPostEdit(post) {
			if (!post.id) {
				return
			}
			this.$router.push(`/post/${post.id}/edit`)
		},
		playVideo() {
			this.previewMode = false
		},
		createUniqueIframeName(post) {
			const { thread_video_code: videoCode } = post || {}

			return `videoIframe_${videoCode}`
		},
		iframeLoaded() {
			const { thread_video_code: code } = this.post || {}

			this.iframeElement = this.$refs[`videoIframe_${code}`]
		},
		updatePost() {
			if (this.post.poll.display_poll) {
				this.getPost(this.$route.params.id)
			} else {
				this.$router.back()
			}
		},
		async deletePost() {
			const response = await this.removePost(this.post.thread_id)
			if (response) {
				this.toggleDeleteModal()
				this.$router.back()
			}
		},
		toggleDeleteModal() {
			this.togglePostDeleteModal()
		},
		handlePostLinkClick() {
			this.sendTrigger({
				category: 'Content',
				action: 'click_link_news_post',
				module: 'News Feed',
				data: this.post.thread_id,
				title: this.post.thread_title,
				content_chapter_id: this.post.chapter_id ? [this.post.chapter_id] : null,
			})
		},
	},
	created() {
		if (this.post) {
			this.likes = this.post.upvote_count
			this.commentsCount = this.post.comments_count
			this.isLiked = this.post.upvote
			this.isFollowed = this.post.follow
		}

		if (+this.$route.query.enable_all_comments) {
			this.enableAllComments = true
		}
	},
	beforeDestroy() {
		this.setMyPosts(false)
	},
}
</script>

<style lang="scss">
.posts_buttons {
	margin-top: 6px;
	padding: 16px 0;
	display: flex;
	justify-content: flex-end;
	align-items: center;
}

.post-card {
	border-radius: 10px !important;
	background: #ffffff !important;
	box-shadow: unset !important;

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 10px !important;
		background: #ffffff !important;
		border: 1px solid #e2e2e2;
	}

	&__content-inner {
		color: #4a4a4a;
		font-family: Inter, sans-serif;
		font-weight: 400;
		font-size: 16px;
		line-height: 140%;
		letter-spacing: 0;
		padding-top: 8px;

		img {
			width: 100%;
		}

		span {
			white-space: normal;
		}

		p {
			margin-bottom: 16px !important;
      &:last-child {
        margin-bottom: 0 !important;
      }
		}

		h1 {
			font-size: 30px;
			padding-bottom: 8px;
			line-height: 30px;
		}
		a {
			line-height: 100% !important;
			letter-spacing: 0 !important;
			font-family: Inter, sans-serif !important;
			font-size: 16px !important;
			font-weight: 400 !important;
			color: var(--link) !important;
			text-decoration: none !important;
		}
	}

	&__meta {
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 18px;
		line-height: 100%;
		color: var(--username-category-title);
		letter-spacing: 0;
		gap: 4px;
		word-break: break-word;

		&-bottom {
			max-width: 350px;
			width: 100%;
			color: var(--company-position-large);
			font-size: 15px;
			font-family: Inter, sans-serif;
			font-weight: 400;
			line-height: 20px;
			letter-spacing: 0;
			word-break: break-word;
		}

		&-time {
			color: rgb(155, 155, 155);
			font-size: 12px;
			font-family: Inter, sans-serif;
			font-weight: normal;
			text-align: right;
		}
	}

	&__avatar {
		border-radius: 8px;
		img {
			object-fit: cover;
		}
	}

	&__outer {
	}

	&__list {
		position: relative;
		flex-direction: column;
	}

	&__link {
		color: var(--ceil-detail-text-color);
		font-size: 16px;
		font-family: Inter, sans-serif;
		font-weight: bold;
		text-decoration: underline;
		cursor: pointer;

		a {
			color: var(--ceil-detail-text-color) !important;
		}
	}

	&__title {
		color: var(--username-category-title);
		font-size: 20px;
		font-family: Inter, sans-serif;
		font-weight: 700;
		letter-spacing: 0;
		line-height: 22px;
		font-style: normal;

		a {
			color: #202232 !important;
			text-decoration: none;
		}
	}

	&__block-view {
		flex-direction: column;
		height: 73%;

		.post-preview-card__thumbnail-outer img {
			max-width: 100%;
		}
	}

	&__content {
		font-family: Inter, sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 16px;
		line-height: 21px;
	}

	&__thumbnail {
		max-height: 311px;
		height: 100%;
		width: 100%;
		object-fit: cover;
	}

	&__tag {
		padding: 6px;
		font-size: 14px !important;
		font-family: Inter, sans-serif;
		font-weight: normal;
		border-radius: 20px !important;
		border: 1.5px solid #e2e2e2 !important;
		height: 20px !important;
		cursor: pointer;
		background-color: #ffffff !important;

		span {
			min-width: 100% !important;
			width: 100%;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
			font-size: 13px;
			color: #002855;
		}
	}

	&__like-button {
		font-family: Inter, sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 13px;
		line-height: 16px;
		color: #aaaaaa;

		&-active {
			color: #db7842 !important;
		}

		.theme--light.v-icon:focus::after {
			opacity: 0;
		}
	}

	&__share-button {
		font-family: Inter, sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 13px;
		line-height: 16px;
		color: #aaaaaa !important;
		cursor: pointer;

		.v-icon {
			transform: rotateY(180deg);
		}

		&:hover {
			span {
				color: #757575;
				text-decoration: underline;
			}
		}
	}

	&__book-button {
		color: var(--accent-color);
		font-size: 14px;
		font-family: Inter, sans-serif;
		font-weight: bold;
		text-decoration: none;

		&-active {
			color: var(--accent-color) !important;
			text-decoration: underline;
		}
	}

	&__comments-view {
		background-color: #f3f3f6 !important;
	}

	&__comment {
		color: rgb(74, 74, 74);
		background-color: rgb(246, 246, 246);
		font-size: 15px;
		font-family: Inter, sans-serif;
		font-weight: normal;
		letter-spacing: -0.43px;
		width: 100%;

		&::placeholder {
			color: rgb(160, 160, 160);
			font-size: 15px;
			font-weight: normal;
			letter-spacing: -0.43px;
		}

		&:focus {
			outline: none;
		}

		&-send {
			width: 38px;
			height: 22px;
			color: rgb(160, 160, 160);
			font-size: 14px;
			font-family: Inter, sans-serif;
			font-weight: bold;
			text-decoration: underline;
			cursor: pointer;
		}
	}

	&__comments-button {
		font-family: Inter, sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 13px;
		line-height: 16px;
		display: flex;
		align-items: flex-end;
		color: #aaaaaa;
		cursor: pointer;

		&:hover {
			span {
				color: #757575;
				text-decoration: underline;
			}
		}
	}

	&__my-menu {
		margin-top: 20px;
		min-width: 175px !important;
		contain: initial !important;
		overflow: visible !important;

		&:before {
			content: '';
			position: absolute;
			left: 14px;
			top: 0;
			z-index: -1;
			border-radius: 3px;
			transform: translate(0, -50%) rotate(45deg);
			width: 20px;
			height: 20px;
			background: #fff;
			box-shadow: 0 0 11px rgb(0 0 0 / 14%);
		}
	}

	&__menu-dots {
		margin-left: 10px !important;
	}

	&__thread-action {
		display: flex;
		flex-wrap: nowrap;
		align-items: center;
		justify-content: space-between;
		text-decoration: none;
		padding: 0 16px 8px 16px;
		border-bottom: 1px solid #eaeaea;

		&-title {
      color: var(--username-category-title);
			font-family: Inter, sans-serif;
			font-size: 14px;
			font-weight: 700;
			line-height: 22px;
			text-decoration: underline;
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 1;
			-webkit-box-orient: vertical;
		}

		&-icon {
			color: #cecece !important;
		}
	}

	@media screen and (max-width: 1904px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 75%;
		}
	}
	@media screen and (max-width: 1264px) {
		&__block {
			flex: 0 0 100%;
		}
		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}
	@media screen and (max-width: 768px) {
		&__thumbnail {
			height: 100%;
			max-width: 100%;
		}
		&__default-thumbnail {
			max-height: 250px;
			height: 100%;
		}
		&__outer {
			flex-direction: column;
		}
	}
}

.dropdown-profile {
	padding: 15px;

	&__item {
		min-height: 30px !important;
		padding: 0;
	}

	&__title {
		color: #aaaaaa;
		font-size: 12px !important;
		line-height: 1.4 !important;
		font-family: 'Montserrat', sans-serif !important;
		font-weight: 600 !important;
	}

	&__title:hover {
		text-decoration: underline;
		color: black;
		transition: color 0.2s, background-color 0.2s, border-color 0.2s !important;
	}
}

.disabled {
	pointer-events: none;
}

.page-title {
	&__actions {
		font-size: 14px;
		font-weight: 700;
		text-transform: capitalize;
		text-decoration: underline;
		line-height: 17.07px;
		font-family: Montserrat, sans-serif;

		&-wrapper {
			background-color: transparent !important;
			display: flex;
			justify-content: flex-end;
		}
	}
}

.post-preview-card__book-button {
	text-decoration-line: underline;
}
</style>
